import { useMemo } from 'react';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import { GroupForm, type GroupFormValues } from '@/components/organisms/settings-groups/GroupForm';
import {
  useAddGroupMembers,
  useGroupDetail,
  useRemoveGroupMember,
  useUpdateGroup,
} from '@/hooks/settings/useGroups';
import { useUsers } from '@/hooks/settings/useUsers';
import type { UpdateGroupDto } from '@/types/settings';

export const Route = createFileRoute('/_dashboard/dashboard/settings/groups/$id/edit')({
  component: EditGroupRoute,
});

function EditGroupRoute() {
  const { id } = Route.useParams();
  const router = useRouter();
  const decodedId = decodeURIComponent(id);

  const { data: usersData } = useUsers();
  const users = useMemo(() => usersData?.data ?? [], [usersData?.data]);
  const { data: group, isLoading: groupLoading } = useGroupDetail(decodedId);
  const updateGroup = useUpdateGroup();
  const addMembers = useAddGroupMembers();
  const removeMember = useRemoveGroupMember();

  const isPending = updateGroup.isPending || addMembers.isPending || removeMember.isPending;

  function goToDetail() {
    void router.navigate({
      to: '/dashboard/settings/groups/$id',
      params: { id: decodedId },
    });
  }

  async function handleUpdate(groupId: string, values: GroupFormValues, nextMemberIds: string[]) {
    if (!group) return;

    const payload: UpdateGroupDto = {
      name: values.name,
      description: values.description ?? '',
      managerId: values.managerId ?? null,
    };
    const currentMemberIds = group.members.map((member) => member.membershipId);
    const nextMemberSet = new Set(nextMemberIds);
    const currentMemberSet = new Set(currentMemberIds);
    const memberIdsToAdd = nextMemberIds.filter((memberId) => !currentMemberSet.has(memberId));
    const memberIdsToRemove = currentMemberIds.filter((memberId) => !nextMemberSet.has(memberId));

    await updateGroup.mutateAsync({ groupId, ...payload });

    if (memberIdsToAdd.length > 0) {
      await addMembers.mutateAsync({ groupId, membershipIds: memberIdsToAdd });
    }

    for (const membershipId of memberIdsToRemove) {
      await removeMember.mutateAsync({ groupId, membershipId });
    }

    goToDetail();
  }

  return (
    <GroupForm
      mode="edit"
      group={group ?? null}
      users={users}
      isLoadingGroup={groupLoading && !group}
      isPending={isPending}
      onBack={goToDetail}
      onSubmit={(values, memberIds) => {
        if (!group) return;
        void handleUpdate(group.id, values, memberIds).catch(() => undefined);
      }}
    />
  );
}
